import { useMemo } from "react";
import { attachmentsListParams } from "@/features/attachments/lib/list-query-params";
import type {
	AttachmentsAdapter,
	AttachmentsDeleteVars,
	AttachmentsEditVars,
	AttachmentsUploadVars,
} from "@/features/attachments/types";
import {
	useDeleteJobFile,
	useEditJobFile,
	useUploadJobFile,
} from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";

/**
 * Pure list query options for job attachments (route loaders).
 */
export function createJobAttachmentsListQueryOptions(args: {
	jobId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return jobQueryOptions.files.list(
		args.jobId,
		args.start,
		args.count,
		attachmentsListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Job-scoped attachments adapter (hooks + query option factories).
 */
export function useJobAttachmentsAdapter(jobId: string): AttachmentsAdapter {
	const uploadJob = useUploadJobFile();
	const deleteJob = useDeleteJobFile();
	const editJob = useEditJobFile();

	return useMemo(
		(): AttachmentsAdapter => ({
			kind: "job",
			entityId: jobId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createJobAttachmentsListQueryOptions({
					jobId,
					organizationId,
					start,
					count,
				}),
			contentQueryOptions: ({ organizationId, fileInfoId }) =>
				jobQueryOptions.files.content(jobId, fileInfoId, { organizationId }),
			thumbnailQueryOptions: ({ organizationId, fileInfoId }) =>
				jobQueryOptions.files.thumbnail(jobId, fileInfoId, {
					organizationId,
				}),
			uploadMutation: {
				mutateAsync: (vars: AttachmentsUploadVars) =>
					uploadJob.mutateAsync({
						jobId: vars.entityId,
						file: vars.file,
						organizationId: vars.organizationId,
						description: vars.description,
						entityAttachmentRequirementTagId:
							vars.entityAttachmentRequirementTagId,
						onUploadProgress: vars.onUploadProgress,
					}),
				reset: () => uploadJob.reset(),
				isPending: uploadJob.isPending,
				error: (uploadJob.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: AttachmentsDeleteVars) =>
					deleteJob.mutateAsync({
						jobId: vars.entityId,
						fileId: vars.fileId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteJob.reset(),
				isPending: deleteJob.isPending,
				error: (deleteJob.error as Error | null) ?? null,
			},
			editMutation: {
				mutateAsync: (vars: AttachmentsEditVars) =>
					editJob.mutateAsync({
						jobId: vars.entityId,
						fileId: vars.fileId,
						name: vars.name,
						description: vars.description,
						organizationId: vars.organizationId,
					}),
				reset: () => editJob.reset(),
				isPending: editJob.isPending,
				error: (editJob.error as Error | null) ?? null,
			},
		}),
		[deleteJob, editJob, jobId, uploadJob],
	);
}
